import BaseUiNode from '../../framework/base-ui-node';

/**
 * Page — Especialização de BaseUiNode para telas completas da nova área.
 *
 * Responsabilidades específicas de página:
 *   - auto-mount no DOMContentLoaded
 *   - fallback de root para document.body
 *   - unsaved-changes guard (formulários)
 *
 * Ciclo de vida (herdado de BaseUiNode):
 *   constructor(config)
 *     └─ onInit()          ← setup de estado
 *        └─ bindEvents()   ← ouvintes de DOM
 *           └─ afterViewInit() ← operações pós-render
 *
 * Uso:
 *   class MinhaPage extends Page {
 *       onInit()        { ... }
 *       bindEvents()    { ... }
 *       afterViewInit() { ... }
 *   }
 *   globalThis.minhaPage = new MinhaPage();
 */
class Page extends BaseUiNode {

    /**
     * @param {Object} [config={}]
     * @param {string} [config.componentId]  ID explícito (ex: 'certificados').
     * @param {string} [config.selector]     Seletor CSS da raiz da página.
     */
    constructor(config = {}) {
        // Gera componentId com prefixo 'page' se não fornecido
        if (!config.componentId) {
            config.componentId = BaseUiNode.uid('page');
        }
        super(config);

        // Auto-mount no DOMContentLoaded
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.mount(), { once: true });
        } else {
            this.mount();
        }
    }

    // ─── Root resolve: Page usa document.body como fallback ───────────

    _resolveRoot(config) {
        if (config.root instanceof Element) return config.root;
        if (config.selector) return document.querySelector(config.selector) ?? document.body;
        return document.body;
    }

    // ─── Destroy: inclui cleanup do unsaved guard ─────────────────────

    destroy() {
        if (this._destroyed) return;
        this._disableUnsavedGuard();
        super.destroy();
    }

    // ─── Unsaved-changes guard ──────────────────────────────────────────

    /**
     * Ativa confirmação ao sair quando o formulário tem alterações não salvas.
     * @param {HTMLFormElement} formEl
     */
    _enableUnsavedGuard(formEl) {
        if (!formEl) return;

        this._dirty = false;

        this._onFormInput = () => { this._dirty = true; };
        formEl.addEventListener('input', this._onFormInput);
        formEl.addEventListener('change', this._onFormInput);

        this._onBeforeUnload = (e) => {
            if (!this._dirty) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', this._onBeforeUnload);

        this._guardedForm = formEl;
    }

    /**
     * Limpa o estado dirty (ex: após submit bem-sucedido).
     */
    _clearDirty() {
        this._dirty = false;
    }

    /**
     * Vincula lógica de "selecionar todos" (tri-state) a um container AJAX.
     *
     * Usa MutationObserver para sincronizar o estado após cada carga do DataTable.
     *
     * @param {HTMLElement} container       — div container do DataTable
     * @param {string} selectAllSelector    — seletor do checkbox "todos" (ex: '#nd-cert-select-all')
     * @param {string} itemSelector         — seletor dos checkboxes individuais (ex: 'input[name="certificados[]"]')
     */
    _bindSelectAll(container, selectAllSelector, itemSelector) {
        if (!container) return;

        const syncSelectAll = () => {
            const selectAll = container.querySelector(selectAllSelector);
            if (!selectAll) return;
            const all          = [...container.querySelectorAll(itemSelector)];
            const checkedCount = all.filter(cb => cb.checked).length;
            selectAll.checked       = all.length > 0 && checkedCount === all.length;
            selectAll.indeterminate = checkedCount > 0 && checkedCount < all.length;
        };

        container.addEventListener('change', (e) => {
            if (e.target.matches(selectAllSelector)) {
                const items = [...container.querySelectorAll(itemSelector)];
                items.forEach(cb => { cb.checked = e.target.checked; });
                return;
            }

            if (e.target.matches(itemSelector)) {
                syncSelectAll();
            }
        });

        new MutationObserver(syncSelectAll).observe(container, { childList: true, subtree: false });
    }

    /**
     * Remove os listeners do guard (chamado automaticamente no destroy).
     */
    _disableUnsavedGuard() {
        if (this._guardedForm) {
            this._guardedForm.removeEventListener('input', this._onFormInput);
            this._guardedForm.removeEventListener('change', this._onFormInput);
        }
        if (this._onBeforeUnload) {
            window.removeEventListener('beforeunload', this._onBeforeUnload);
        }
        this._guardedForm = null;
    }
}

export default Page;
